import { useContext, useEffect } from "react";

import { AuthContext } from "~/auth/AuthProvider";
import {
	type GetAllFeatureFlagsQuery,
	GetFeatureFlagDocument,
	type GetFeatureFlagQuery
} from "~/gql/queries/featureFlag.generated";
import {
	OnFeatureFlagChangedDocument,
	type OnFeatureFlagChangedSubscription
} from "~/gql/subscriptions/featureFlag.generated";
import describeSubscriptionClose from "~/helpers/describeSubscriptionClose";
import logger from "~/helpers/logger";
import { useAppDispatch } from "~/hooks/reduxHooks";
import useSubscriptionClient from "~/hooks/useSubscriptionClient";
import client from "~/services/client";
import { apiEndpoints } from "~/services/endpoints/apiEndpoints";
import { recreateSubscriptionClientOnUnauthorized } from "~/services/subscriptionClient";

type FlagKey = Exclude<keyof GetAllFeatureFlagsQuery, "__typename">;

/**
 * Subscribes to real-time feature flag changes. The subscription event only
 * identifies *which* flag changed; the hook refetches that single flag and
 * patches the RTK Query cache in place, avoiding a full refetch of all flags.
 */
const useFeatureFlagSubscription = (): void => {
	const subscriptionClient = useSubscriptionClient();
	const dispatch = useAppDispatch();
	const { isCustomer, loggedIn } = useContext(AuthContext);

	useEffect(() => {
		if (!loggedIn) return;
		if (isCustomer) return;

		const cleanup = subscriptionClient.subscribe<OnFeatureFlagChangedSubscription>(
			{ query: OnFeatureFlagChangedDocument.toString() },
			{
				complete: () => {},
				error: (err) => {
					const closed = describeSubscriptionClose(err);
					recreateSubscriptionClientOnUnauthorized(closed.code);
					logger.error(
						`[FeatureFlag subscription] close code=${closed.code ?? "none"} clean=${closed.wasClean ?? "n/a"} reason=${closed.reason}`,
						err,
						{ ...closed }
					);
					// On subscription error, invalidate the cache so the next mount/focus triggers a full refetch
					dispatch(apiEndpoints.util.invalidateTags(["FeatureFlag"]));
				},
				next: async ({ data }) => {
					const changed = data?.onFeatureFlagChanged;
					if (!changed) return;

					const flagKey = changed.name as unknown as FlagKey;

					try {
						const result = await client.request<GetFeatureFlagQuery>(GetFeatureFlagDocument.toString(), {
							name: changed.name
						});

						dispatch(
							apiEndpoints.util.updateQueryData("GetAllFeatureFlags", undefined, (draft) => {
								const existing = draft[flagKey];
								if (existing) {
									existing.config = result.featureFlag.config;
									existing.enabled = result.featureFlag.enabled;
								}
							})
						);
					} catch (err) {
						logger.error("[FeatureFlag subscription] refetch error:", err);
						// Fall back to invalidating the entire cache
						dispatch(apiEndpoints.util.invalidateTags(["FeatureFlag"]));
					}
				}
			}
		);

		return cleanup;
	}, [isCustomer, loggedIn, subscriptionClient]);
};

export default useFeatureFlagSubscription;
